import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './StudentOnboarding.css';
import { supabase } from './supabaseClient';
import { awardSignupBonus } from './xpService';

const SUBJECTS = [
  'Math', 'Science', 'English', 'Arabic',
  'History', 'Geography', 'Physics', 'Chemistry',
  'Biology', 'Computer Science', 'French', 'Economics',
  'Art', 'Music', 'Physical Education', 'Islamic Studies'
];

const GOAL_EXAMPLES = [
  'Improve exam scores',
  'Get homework help',
  'Prepare for university',
  'Learn at my own pace',
  'Build confidence in the subject'
];

export default function StudentOnboarding({ lang = 'en' }) {
  const [preferences, setPreferences] = useState({
    preferred_subjects: [],
    learning_goals: '',
    preferred_tutoring_type: '',
    preferred_session_type: '',
    preferred_language: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const navigate = useNavigate();

  useEffect(() => {
    checkUser();
  }, []);

  // Auto-advance steps as user completes them
  useEffect(() => {
    if (preferences.preferred_subjects.length > 0 && currentStep === 1) {
      setCurrentStep(2);
    }
    if (preferences.learning_goals.length >= 10 && currentStep === 2) {
      setCurrentStep(3);
    }
    if (preferences.preferred_tutoring_type && preferences.preferred_session_type && currentStep === 3) {
      setCurrentStep(4);
    }
  }, [preferences, currentStep]);

  const checkUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      navigate('/signin');
      return;
    }
    setCurrentUser(user);
  };

  const validateForm = () => {
    const newErrors = {};

    if (preferences.preferred_subjects.length === 0) {
      newErrors.subjects = lang === 'ar'
        ? 'يرجى اختيار مادة واحدة على الأقل'
        : 'Please select at least one subject';
    }

    if (preferences.learning_goals.trim().length < 10) {
      newErrors.goals = lang === 'ar'
        ? 'يرجى كتابة أهدافك التعليمية (10 أحرف على الأقل)'
        : 'Please provide more details (min 10 characters)';
    }

    if (!preferences.preferred_tutoring_type) {
      newErrors.tutoring = lang === 'ar'
        ? 'يرجى اختيار نوع التدريس'
        : 'Please select a tutoring type';
    }

    if (!preferences.preferred_session_type) {
      newErrors.session = lang === 'ar'
        ? 'يرجى اختيار نوع الجلسة'
        : 'Please select a session type';
    }

    if (!preferences.preferred_language) {
      newErrors.language = lang === 'ar'
        ? 'يرجى اختيار اللغة المفضلة'
        : 'Please select a language preference';
    }

    return newErrors;
  };

  const handleSubjectToggle = (subject) => {
    setPreferences(prev => ({
      ...prev,
      preferred_subjects: prev.preferred_subjects.includes(subject)
        ? prev.preferred_subjects.filter(s => s !== subject)
        : [...prev.preferred_subjects, subject]
    }));
    if (errors.subjects) {
      setErrors(prev => ({ ...prev, subjects: '' }));
    }
  };

  const handleChange = (field, value) => {
    setPreferences(prev => ({ ...prev, [field]: value }));
    const errorKey = field.replace('preferred_', '').replace('learning_', '');
    if (errors[errorKey]) {
      setErrors(prev => ({ ...prev, [errorKey]: '' }));
    }
  };

  const handleExampleClick = (example) => {
    const currentGoals = preferences.learning_goals.trim();
    const newGoals = currentGoals
      ? `${currentGoals}, ${example.toLowerCase()}`
      : example;
    handleChange('learning_goals', newGoals);
  };

  const handleSkip = async () => {
    if (!currentUser) return;

    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('students')
        .update({ onboarding_completed: false })
        .eq('uuid_id', currentUser.id);

      if (error) throw error;

      navigate('/dashboard/student/dashboard');
    } catch (error) {
      console.error('Error skipping onboarding:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (!currentUser) return;

    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('students')
        .update({
          onboarding_completed: true,
          preferred_subjects: preferences.preferred_subjects,
          learning_goals: preferences.learning_goals.trim(),
          preferred_tutoring_type: preferences.preferred_tutoring_type,
          preferred_session_type: preferences.preferred_session_type,
          preferred_language: preferences.preferred_language
        })
        .eq('uuid_id', currentUser.id);

      if (error) throw error;

      console.log('Preferences saved successfully');

      // Set flag for dashboard to show welcome notification (set immediately)
      localStorage.setItem('showWelcomeXP', 'true');

      // Award signup bonus (50 XP) - fire and forget
      console.log('🎁 Attempting to award signup bonus for student:', currentUser.id);
      awardSignupBonus(currentUser.id, currentUser.email)
        .then(({ data, error }) => {
          console.log('📊 Signup bonus response:', { data, error });
          if (data && !error) {
            console.log('✅ Signup bonus awarded successfully:', data);
          } else if (error) {
            console.error('❌ Signup bonus error:', error);
          }
        })
        .catch(err => {
          console.error('❌ Signup bonus exception:', err);
        });

      navigate('/dashboard/student/dashboard');
    } catch (error) {
      console.error('Error saving preferences:', error);
      alert(lang === 'ar' ? 'حدث خطأ أثناء الحفظ. يرجى المحاولة مرة أخرى.' : 'Error saving preferences. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const text = {
    en: {
      title: "Let's personalize your learning experience!",
      subtitle: "Help us match you with the perfect tutors by sharing your preferences.",
      step: "Step",
      of: "of",
      subjects: "Your Subjects",
      subjectsHint: "Pick your top interests",
      subjectsDesc: "Select the subjects you want to learn or improve",
      goals: "Your Learning Goals",
      goalsHint: "Tell us what you want to achieve",
      goalsPlaceholder: "What do you hope to achieve?",
      examplesLabel: "Quick suggestions:",
      tutoringType: "Learning Format",
      tutoringDesc: "How would you like to learn?",
      online: "Online",
      onlineDesc: "Virtual sessions from anywhere",
      inPerson: "In-person",
      inPersonDesc: "Face-to-face learning",
      both: "Both",
      bothDesc: "Flexible options",
      sessionType: "Session Type",
      sessionDesc: "What kind of sessions do you prefer?",
      individual: "Individual",
      individualDesc: "One-on-one focused attention",
      group: "Group",
      groupDesc: "Learn with peers",
      flexible: "Flexible",
      flexibleDesc: "Open to either option",
      languageLabel: "Language",
      languageDesc: "Preferred language of instruction",
      english: "English",
      arabic: "Arabic",
      bothLang: "Both",
      skip: "Skip for now",
      submit: "Find My Tutors",
      submitting: "Saving preferences..."
    },
    ar: {
      title: "لنخصص تجربتك التعليمية!",
      subtitle: "ساعدنا في مطابقتك مع المعلمين المثاليين من خلال مشاركة تفضيلاتك.",
      step: "خطوة",
      of: "من",
      subjects: "موادك",
      subjectsHint: "اختر اهتماماتك الرئيسية",
      subjectsDesc: "حدد المواد التي تريد تعلمها أو تحسينها",
      goals: "أهدافك التعليمية",
      goalsHint: "أخبرنا بما تريد تحقيقه",
      goalsPlaceholder: "ماذا تأمل أن تحقق؟",
      examplesLabel: "اقتراحات سريعة:",
      tutoringType: "تنسيق التعلم",
      tutoringDesc: "كيف تحب أن تتعلم؟",
      online: "عبر الإنترنت",
      onlineDesc: "جلسات افتراضية من أي مكان",
      inPerson: "شخصياً",
      inPersonDesc: "تعلم وجهاً لوجه",
      both: "كلاهما",
      bothDesc: "خيارات مرنة",
      sessionType: "نوع الجلسة",
      sessionDesc: "ما نوع الجلسات التي تفضلها؟",
      individual: "فردي",
      individualDesc: "اهتمام مركز واحد لواحد",
      group: "جماعي",
      groupDesc: "تعلم مع الأقران",
      flexible: "مرن",
      flexibleDesc: "مفتوح لأي خيار",
      languageLabel: "اللغة",
      languageDesc: "لغة التدريس المفضلة",
      english: "الإنجليزية",
      arabic: "العربية",
      bothLang: "كلاهما",
      skip: "تخطي الآن",
      submit: "ابحث عن معلميني",
      submitting: "جاري حفظ التفضيلات..."
    }
  };

  const t = text[lang] || text.en;

  return (
    <div className={`onboarding-container ${lang === 'ar' ? 'rtl' : ''}`}>
      <div className="onboarding-content">
        {/* Header with Progress */}
        <div className="onboarding-header">
          <div className="progress-indicator">
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${(currentStep / 4) * 100}%` }}
              />
            </div>
            <p className="progress-text">
              {t.step} {currentStep} {t.of} 4
            </p>
          </div>
          <h1>{t.title}</h1>
          <p className="onboarding-subtitle">{t.subtitle}</p>
        </div>

        <form onSubmit={handleSubmit} className="onboarding-form">
          {/* Section 1: Subjects */}
          <div className={`form-section-card ${currentStep >= 1 ? 'active' : ''}`}>
            <div className="section-header">
              <div className="section-number">1</div>
              <div>
                <h2 className="section-title">{t.subjects}</h2>
                <p className="section-description">{t.subjectsDesc}</p>
              </div>
            </div>

            <p className="section-hint">{t.subjectsHint}</p>

            <div className="subjects-grid">
              {SUBJECTS.map((subject) => (
                <button
                  key={subject}
                  type="button"
                  className={`subject-chip ${preferences.preferred_subjects.includes(subject) ? 'selected' : ''}`}
                  onClick={() => handleSubjectToggle(subject)}
                >
                  {subject}
                  {preferences.preferred_subjects.includes(subject) && (
                    <span className="check-icon">✓</span>
                  )}
                </button>
              ))}
            </div>
            {errors.subjects && <p className="error-message">{errors.subjects}</p>}
          </div>

          {/* Section 2: Learning Goals */}
          <div className={`form-section-card ${currentStep >= 2 ? 'active' : ''}`}>
            <div className="section-header">
              <div className="section-number">2</div>
              <div>
                <h2 className="section-title">{t.goals}</h2>
                <p className="section-description">{t.goalsHint}</p>
              </div>
            </div>

            <textarea
              className={`goals-textarea ${errors.goals ? 'error' : ''}`}
              placeholder={t.goalsPlaceholder}
              value={preferences.learning_goals}
              onChange={(e) => handleChange('learning_goals', e.target.value)}
              maxLength={500}
              rows={4}
            />

            <div className="goals-footer">
              <div className="example-chips">
                <span className="examples-label">{t.examplesLabel}</span>
                {GOAL_EXAMPLES.map((example) => (
                  <button
                    key={example}
                    type="button"
                    className="example-chip"
                    onClick={() => handleExampleClick(example)}
                  >
                    + {example}
                  </button>
                ))}
              </div>
              <div className="char-count">
                {preferences.learning_goals.length}/500
              </div>
            </div>
            {errors.goals && <p className="error-message">{errors.goals}</p>}
          </div>

          {/* Section 3: Format Preferences */}
          <div className={`form-section-card ${currentStep >= 3 ? 'active' : ''}`}>
            <div className="section-header">
              <div className="section-number">3</div>
              <div>
                <h2 className="section-title">{t.tutoringType} & {t.sessionType}</h2>
                <p className="section-description">{t.tutoringDesc}</p>
              </div>
            </div>

            {/* Tutoring Type */}
            <div className="radio-group-compact">
              <label className={`radio-card ${preferences.preferred_tutoring_type === 'online' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="tutoring_type"
                  value="online"
                  checked={preferences.preferred_tutoring_type === 'online'}
                  onChange={(e) => handleChange('preferred_tutoring_type', e.target.value)}
                />
                <div className="radio-content">
                  <span className="radio-icon">🌐</span>
                  <div>
                    <div className="radio-label">{t.online}</div>
                    <div className="radio-desc">{t.onlineDesc}</div>
                  </div>
                </div>
                {preferences.preferred_tutoring_type === 'online' && (
                  <span className="selected-badge">✓</span>
                )}
              </label>

              <label className={`radio-card ${preferences.preferred_tutoring_type === 'in-person' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="tutoring_type"
                  value="in-person"
                  checked={preferences.preferred_tutoring_type === 'in-person'}
                  onChange={(e) => handleChange('preferred_tutoring_type', e.target.value)}
                />
                <div className="radio-content">
                  <span className="radio-icon">🏫</span>
                  <div>
                    <div className="radio-label">{t.inPerson}</div>
                    <div className="radio-desc">{t.inPersonDesc}</div>
                  </div>
                </div>
                {preferences.preferred_tutoring_type === 'in-person' && (
                  <span className="selected-badge">✓</span>
                )}
              </label>

              <label className={`radio-card ${preferences.preferred_tutoring_type === 'both' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="tutoring_type"
                  value="both"
                  checked={preferences.preferred_tutoring_type === 'both'}
                  onChange={(e) => handleChange('preferred_tutoring_type', e.target.value)}
                />
                <div className="radio-content">
                  <span className="radio-icon">🔄</span>
                  <div>
                    <div className="radio-label">{t.both}</div>
                    <div className="radio-desc">{t.bothDesc}</div>
                  </div>
                </div>
                {preferences.preferred_tutoring_type === 'both' && (
                  <span className="selected-badge">✓</span>
                )}
              </label>
            </div>
            {errors.tutoring && <p className="error-message">{errors.tutoring}</p>}

            {/* Session Type */}
            <div className="subsection-divider"></div>
            <p className="section-description">{t.sessionDesc}</p>

            <div className="radio-group-compact">
              <label className={`radio-card ${preferences.preferred_session_type === 'individual' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="session_type"
                  value="individual"
                  checked={preferences.preferred_session_type === 'individual'}
                  onChange={(e) => handleChange('preferred_session_type', e.target.value)}
                />
                <div className="radio-content">
                  <span className="radio-icon">👤</span>
                  <div>
                    <div className="radio-label">{t.individual}</div>
                    <div className="radio-desc">{t.individualDesc}</div>
                  </div>
                </div>
                {preferences.preferred_session_type === 'individual' && (
                  <span className="selected-badge">✓</span>
                )}
              </label>

              <label className={`radio-card ${preferences.preferred_session_type === 'group' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="session_type"
                  value="group"
                  checked={preferences.preferred_session_type === 'group'}
                  onChange={(e) => handleChange('preferred_session_type', e.target.value)}
                />
                <div className="radio-content">
                  <span className="radio-icon">👥</span>
                  <div>
                    <div className="radio-label">{t.group}</div>
                    <div className="radio-desc">{t.groupDesc}</div>
                  </div>
                </div>
                {preferences.preferred_session_type === 'group' && (
                  <span className="selected-badge">✓</span>
                )}
              </label>

              <label className={`radio-card ${preferences.preferred_session_type === 'flexible' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="session_type"
                  value="flexible"
                  checked={preferences.preferred_session_type === 'flexible'}
                  onChange={(e) => handleChange('preferred_session_type', e.target.value)}
                />
                <div className="radio-content">
                  <span className="radio-icon">⚡</span>
                  <div>
                    <div className="radio-label">{t.flexible}</div>
                    <div className="radio-desc">{t.flexibleDesc}</div>
                  </div>
                </div>
                {preferences.preferred_session_type === 'flexible' && (
                  <span className="selected-badge">✓</span>
                )}
              </label>
            </div>
            {errors.session && <p className="error-message">{errors.session}</p>}
          </div>

          {/* Section 4: Language */}
          <div className={`form-section-card ${currentStep >= 4 ? 'active' : ''}`}>
            <div className="section-header">
              <div className="section-number">4</div>
              <div>
                <h2 className="section-title">{t.languageLabel}</h2>
                <p className="section-description">{t.languageDesc}</p>
              </div>
            </div>

            <div className="radio-group-compact">
              <label className={`radio-card ${preferences.preferred_language === 'english' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="language"
                  value="english"
                  checked={preferences.preferred_language === 'english'}
                  onChange={(e) => handleChange('preferred_language', e.target.value)}
                />
                <div className="radio-content">
                  <span className="radio-icon">🇬🇧</span>
                  <div>
                    <div className="radio-label">{t.english}</div>
                  </div>
                </div>
                {preferences.preferred_language === 'english' && (
                  <span className="selected-badge">✓</span>
                )}
              </label>

              <label className={`radio-card ${preferences.preferred_language === 'arabic' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="language"
                  value="arabic"
                  checked={preferences.preferred_language === 'arabic'}
                  onChange={(e) => handleChange('preferred_language', e.target.value)}
                />
                <div className="radio-content">
                  <span className="radio-icon">🇸🇦</span>
                  <div>
                    <div className="radio-label">{t.arabic}</div>
                  </div>
                </div>
                {preferences.preferred_language === 'arabic' && (
                  <span className="selected-badge">✓</span>
                )}
              </label>

              <label className={`radio-card ${preferences.preferred_language === 'both' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="language"
                  value="both"
                  checked={preferences.preferred_language === 'both'}
                  onChange={(e) => handleChange('preferred_language', e.target.value)}
                />
                <div className="radio-content">
                  <span className="radio-icon">🌍</span>
                  <div>
                    <div className="radio-label">{t.bothLang}</div>
                  </div>
                </div>
                {preferences.preferred_language === 'both' && (
                  <span className="selected-badge">✓</span>
                )}
              </label>
            </div>
            {errors.language && <p className="error-message">{errors.language}</p>}
          </div>

          {/* Action Buttons */}
          <div className="form-actions">
            <button
              type="button"
              className="skip-button"
              onClick={handleSkip}
              disabled={isLoading}
            >
              {t.skip}
            </button>
            <button
              type="submit"
              className="submit-button"
              disabled={isLoading}
            >
              {isLoading ? t.submitting : t.submit}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
